// media.js - Where real photographs get switched on.
//
// Two kinds of photograph, and they are used differently.
//
// `PHOTOS` are framed scenes: the bench, a scale, hands weighing something. A
// component asks for one by slot name and falls back to a drawing when the slot
// is empty, so switching one on is a one line change and switching it off again
// is too. Most of these are still empty.
//
// `KIT` is the kit photography: our own printed pieces and the countertop
// display, shot with the background removed. They are objects rather than
// scenes, which is why they are separate. A cutout sits directly on the paper
// ground with a halo behind it and needs no frame, no crop and no border, and it
// carries the colour of the real thing rather than the colour of a drawing. They
// are what most of the site should lean on.
//
// Both are written by client/kit.js into public/images/kit at three widths, and
// srcSet() below builds the srcset from that same width list. Run
// `npm --prefix client run kit` after changing a master; the build does not.
//
// Nothing here should be a stock image. A stock photograph of a gold necklace
// undoes the whole point of the design.
//
// LEGEND
//   1. KIT         our own kit and display, cut out
//   2. PHOTOS      framed scenes, mostly still to be shot
//   3. hasPhoto()  whether a slot is filled
//   4. srcSet()    build a responsive srcset from the sized files

////////// 1. THE KIT, CUT OUT //////////
// Masters live in src/assets/kitPhotography under these same names. The comment
// against each one is what it actually shows, so a page can pick by reading
// rather than by opening thirteen files.

const KIT_DIR = "/images/kit";

export const KIT = {
  // THE MAILER. This is the product, and the thing to reach for by default.
  // It is a printed rigid envelope: the front carries the offer, the back is a
  // plain flap. Somebody takes one off a counter or gets one in the post, puts
  // their jewellery in it, and sends it back. It is not a leaflet, and calling
  // it one put the wrong picture in several places.
  // The hero shot: one mailer square to the camera, cut from mailerFront. Use
  // this anywhere the mailer is the subject; the full stack is a 1 to 2 frame
  // that no page column is shaped like.
  mailerHero: `${KIT_DIR}/mailer-hero.webp`,

  mailerFront: `${KIT_DIR}/mailers-front.webp`,
  mailerStack: `${KIT_DIR}/mailers-stack.webp`,
  mailerRight: `${KIT_DIR}/mailers-right.webp`,
  mailerLeft: `${KIT_DIR}/mailers-left.webp`,
  // The best of them for anything that has to persuade: a three quarter stack
  // with real depth, and the bottom third of every mailer is a photograph of
  // the scrap gold we buy.
  mailerTilt: `${KIT_DIR}/mailers-tilt.webp`,

  // EXPLAINER, NOT MARKETING. Opened out: the packing guide, the prepaid FedEx
  // envelope, the booklet and the mailer, laid flat and evenly lit. It answers
  // "what is in one" for somebody who has already decided, and it is flat and
  // busy in exactly the way a picture meant to persuade must not be. Use it
  // where a question is being answered, never on a hero, a band or a card whose
  // job is to sell. The same goes for `literature` and `standBack`.
  contents: `${KIT_DIR}/kit-contents.webp`,

  // The booklets and the metals guide, close in. Nearly square.
  literature: `${KIT_DIR}/kit-literature.webp`,

  // Mailers and booklets fanned out flat. Square.
  spread: `${KIT_DIR}/kit-spread.webp`,

  // The countertop display, which is the affiliate piece. Tall and narrow.
  stand: `${KIT_DIR}/display-stand.webp`,
  standAngle: `${KIT_DIR}/display-stand-angle.webp`,
  standBack: `${KIT_DIR}/display-stand-back.webp`,
  standStocked: `${KIT_DIR}/display-stand-stocked.webp`,
  standMailers: `${KIT_DIR}/display-stand-mailers.webp`,
};

////////// 2. FRAMED SCENES //////////
// Still to be shot. Daylight, on the warm paper background where possible, at
// the width noted against each slot.

export const PHOTOS = {
  // 1600x1200. The bench, a scale, a few pieces mid-evaluation. Landscape.
  hero: "",

  // 1200x900. The mailer as it arrives, unopened, on a doorstep or a table.
  // The product shot stands in until that exists, which is closer than the
  // flat lay was: this slot is captioned "what turns up", not "what is in it".
  kit: KIT.mailerHero,

  // 1200x900. A package being opened on camera, the camera visible in shot.
  unboxing: "",

  // 1200x900. Hands weighing a single piece, scale display readable.
  weighing: "",

  // 1200x900. An acid test or the XRF, close in.
  testing: "",

  // 800x800 each. One per category on the what we buy page.
  itemsGold: "",
  itemsSilver: "",
  itemsPlatinum: "",

  // 1200x800. The team, or the room. Anything true and not a stock office.
  about: "",
};

////////// 3. AVAILABILITY //////////

export const hasPhoto = (key) => Boolean(PHOTOS[key]);

////////// 4. RESPONSIVE SOURCES //////////
// A path of "/images/kit/kit-contents.webp" has kit-contents-400.webp and its
// siblings written beside it by client/kit.js. This builds the srcset for them.
// When they do not exist the browser simply uses the single src.
//
// It takes either a PHOTOS slot name or a path, because the kit photography is
// referenced directly rather than through a slot.

export function srcSet(keyOrPath, widths = [400, 800, 1200]) {
  const path = PHOTOS[keyOrPath] ?? keyOrPath;
  if (!path || typeof path !== "string") return undefined;

  const dot = path.lastIndexOf(".");
  if (dot === -1) return undefined;

  const stem = path.slice(0, dot);
  const extension = path.slice(dot);
  return widths.map((width) => `${stem}-${width}${extension} ${width}w`).join(", ");
}
